import {
  createContext,
  PropsWithChildren,
  ReactElement,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import ContentLoader from 'react-content-loader';
import { AutoSizer } from 'react-virtualized-auto-sizer';
import { Neutral200, Neutral300 } from '../../utils/colors';

const SkeletonContext = createContext({
  width: 0,
  height: 0
});

interface Rect {
  x: string;
  y: string;
  width: string;
  height: string;
}

const SkeletonContent = ({
  width,
  height,
  dataTestId,
  children
}: PropsWithChildren<{ width: number; height: number; dataTestId?: string }>) => {
  const contextValue = useMemo(() => ({ width, height }), [width, height]);
  return (
    <ContentLoader
      preserveAspectRatio='none'
      backgroundColor={Neutral200}
      foregroundColor={Neutral300}
      style={{ width, height }}
      width={width}
      height={height}
      data-testid={dataTestId}
    >
      <SkeletonContext.Provider value={contextValue}>{children}</SkeletonContext.Provider>
    </ContentLoader>
  );
};

interface SkeletonProps extends PropsWithChildren {
  // ContentLoader forwards unrecognized props onto its root <svg>, so this reaches the DOM --
  // lets a test assert the loading state is showing (e.g. during a delayed intercept response).
  dataTestId?: string;
}

export const Skeleton = ({ dataTestId, children }: SkeletonProps) => (
  <AutoSizer
    renderProp={({ width = 0, height = 0 }) => (
      <SkeletonContent width={width} height={height} dataTestId={dataTestId}>
        {children}
      </SkeletonContent>
    )}
  />
);

const DEFAULT_AXIS_SIZE = 8;

interface AxesSkeletonProps {
  size?: number;
}

export const AxesSkeleton = ({ size = DEFAULT_AXIS_SIZE }: AxesSkeletonProps): ReactElement => (
  <>
    <rect x={0} y={0} width={`${size}px`} height='100%' />
    <rect x={0} y={`calc(100% - ${size}px)`} width='100%' height={`${size}px`} />
  </>
);

const DEFAULT_BAR_WIDTH = 20;

// Deterministic PRNG for stable snapshots (e.g. Chromatic). Quality is sufficient
// for cosmetic bar heights; do not use for anything requiring uniform distribution.
const createSeededRandom = (seed: number) => {
  let n = seed;
  return () => {
    n += 1;
    const x = Math.sin(n) * 10000;
    return x - Math.floor(x);
  };
};

interface BarGraphSkeletonProps {
  barWidth?: number;
  gutter?: number;
  horizontalPadding?: number;
  verticalPadding?: number;
  seed?: number;
}

// TODO: make this have no gutter on the outside
export const BarGraphSkeleton = ({
  barWidth = DEFAULT_BAR_WIDTH,
  gutter = DEFAULT_BAR_WIDTH,
  horizontalPadding = 0,
  verticalPadding = 0,
  seed
}: BarGraphSkeletonProps) => {
  const { width, height } = useContext(SkeletonContext);
  const availableWidth = width - gutter - horizontalPadding * 2;
  const numBars = Math.floor(availableWidth / (gutter + barWidth));

  const random = useMemo(() => (seed != null ? createSeededRandom(seed) : Math.random), [seed]);

  const [barHeightRatios, setBarHeightRatios] = useState<number[]>(() =>
    Array.from({ length: numBars }, () => 0.25 + random() * 0.75)
  );

  // Keep the old random height ratios so we don't make everything rerender with random heights
  // every time the dimensions change. When the width changes if we need new bars, just add them.
  useEffect(() => {
    if (numBars > barHeightRatios.length) {
      const additionalCount = numBars - barHeightRatios.length;
      const newRatios = Array.from({ length: additionalCount }, () => 0.25 + random() * 0.75);

      setBarHeightRatios(prev => [...prev, ...newRatios]);
    }
  }, [numBars, barHeightRatios.length, random]);

  const barData = useMemo(() => {
    const extra = Math.floor((availableWidth % (barWidth + gutter)) / 2);
    let x = extra + gutter + horizontalPadding;

    const totalHeight = height - verticalPadding * 2;

    const bars: Rect[] = [];
    for (let i = 0; i < numBars; ++i) {
      const barHeight = (barHeightRatios[i] || 0) * totalHeight;
      bars.push({
        x: `${x}px`,
        y: `${verticalPadding + totalHeight - barHeight}px`,
        width: `${barWidth}px`,
        height: `${barHeight}px`
      });
      x += barWidth + gutter;
    }
    return bars;
  }, [
    height,
    horizontalPadding,
    verticalPadding,
    gutter,
    barWidth,
    numBars,
    barHeightRatios,
    availableWidth
  ]);

  return (
    <>
      {barData.map((props, i) => (
        <rect key={i} {...props} />
      ))}
    </>
  );
};

const DEFAULT_ITEM_SIZE = 50;

interface ItemListSkeletonProps {
  size?: number;
  gutter?: number;
  horizontalPadding?: number;
  verticalPadding?: number;
}

export const ItemListSkeleton = ({
  size = DEFAULT_ITEM_SIZE,
  gutter = DEFAULT_ITEM_SIZE,
  horizontalPadding = 0,
  verticalPadding = 0
}: ItemListSkeletonProps) => {
  const { width } = useContext(SkeletonContext);
  const availableWidth = width - gutter - horizontalPadding * 2;
  const numItems = Math.floor(availableWidth / (gutter + size));

  const itemData = useMemo(() => {
    const extra = Math.floor((availableWidth % (size + gutter)) / 2);
    let x = extra + gutter + horizontalPadding;

    const items: Rect[] = [];
    for (let i = 0; i < numItems; ++i) {
      items.push({
        x: `${x}px`,
        y: `${verticalPadding}px`,
        width: `${size}px`,
        height: `${size}px`
      });
      x += size + gutter;
    }
    return items;
  }, [size, gutter, horizontalPadding, verticalPadding, availableWidth, numItems]);

  return (
    <>
      {itemData.map((props, i) => (
        <rect key={i} {...props} />
      ))}
    </>
  );
};

const DEFAULT_LINE_HEIGHT = 10;

type DimensionGetter = (dim: number) => number;
interface ListSkeletonProps {
  lineHeight?: number;
  lineSpacing?: number;
  horizontalPadding?: number;
  verticalPadding?: number;
  borderRadius?: number;
  alignTop?: boolean;
  width?: number | DimensionGetter;
  height?: number | DimensionGetter;
  x?: number | DimensionGetter;
  y?: number | DimensionGetter;
}

export const ListSkeleton = ({
  lineHeight = DEFAULT_LINE_HEIGHT,
  lineSpacing = DEFAULT_LINE_HEIGHT,
  horizontalPadding = 0,
  verticalPadding = 0,
  borderRadius = lineHeight / 2,
  alignTop = false,
  width: widthProp,
  height: heightProp,
  x = 0,
  y = 0
}: ListSkeletonProps) => {
  const { width, height } = useContext(SkeletonContext);

  let ourWidth = width;
  if (widthProp != null) {
    ourWidth = widthProp instanceof Function ? widthProp(width) : widthProp;
  }

  let ourHeight = height;
  if (heightProp != null) {
    ourHeight = heightProp instanceof Function ? heightProp(height) : heightProp;
  }
  const ourX = x instanceof Function ? x(width) : x;
  const ourY = y instanceof Function ? y(height) : y;

  const availableHeight = ourHeight - verticalPadding * 2 - lineSpacing;
  const numItems = Math.floor(availableHeight / (lineHeight + lineSpacing));

  const itemData = useMemo(() => {
    const extra = Math.floor((availableHeight % (lineHeight + lineSpacing)) / 2);
    let currentY = ourY + (alignTop ? 0 : extra + lineSpacing) + verticalPadding;

    const items: { y: string }[] = [];
    for (let i = 0; i < numItems; ++i) {
      items.push({ y: `${currentY}px` });
      currentY += lineHeight + lineSpacing;
    }
    return items;
  }, [availableHeight, lineSpacing, verticalPadding, lineHeight, numItems, alignTop, ourY]);

  return (
    <>
      {itemData.map((props, i) => (
        <rect
          key={i}
          {...props}
          rx={borderRadius}
          ry={borderRadius}
          x={`${horizontalPadding + ourX}px`}
          width={`${ourWidth - horizontalPadding * 2}px`}
          height={`${lineHeight}px`}
        />
      ))}
    </>
  );
};

export const ListGroupSkeleton = (props: ListSkeletonProps) => {
  const passProps = {
    lineHeight: 49,
    alignTop: true,
    borderRadius: 5,
    lineSpacing: 4,
    ...props
  };
  return <ListSkeleton {...passProps} />;
};

export const FILTER_BAR_SKELETON_HEIGHT = 38;
const FILTER_BAR_FILTER_BUTTON_WIDTH = 65;
const FILTER_BAR_BASIC_BUTTON_WIDTH = 100;
const FILTER_BAR_BORDER_RADIUS = 2;
const FILTER_BAR_INTERNAL_GUTTER = 8;

interface FilterBarSkeletonProps {
  width?: number | DimensionGetter;
  x?: number | DimensionGetter;
  y?: number | DimensionGetter;
  margin?: number;
}

export const FilterBarSkeleton = ({
  width: widthProp,
  x = 0,
  y = 0,
  margin
}: FilterBarSkeletonProps) => {
  const { width, height } = useContext(SkeletonContext);
  let ourWidth: number = width;
  if (widthProp != null) {
    ourWidth = widthProp instanceof Function ? widthProp(width) : widthProp || 0;
  }

  let ourX = x instanceof Function ? x(width) : x;
  let ourY = y instanceof Function ? y(height) : y;

  if (margin) {
    ourWidth -= margin * 2;
    ourX += margin;
    ourY += margin;
  }
  const filterBarWidth =
    ourWidth -
    FILTER_BAR_INTERNAL_GUTTER * 2 -
    FILTER_BAR_FILTER_BUTTON_WIDTH -
    FILTER_BAR_BASIC_BUTTON_WIDTH;
  return (
    <>
      <rect
        x={`${ourX}px`}
        y={`${ourY}px`}
        width={`${filterBarWidth}px`}
        height={`${FILTER_BAR_SKELETON_HEIGHT}px`}
        ry={FILTER_BAR_BORDER_RADIUS}
        rx={FILTER_BAR_BORDER_RADIUS}
      />
      <rect
        x={`${ourX + filterBarWidth + FILTER_BAR_INTERNAL_GUTTER}px`}
        y={`${ourY}px`}
        width={`${FILTER_BAR_FILTER_BUTTON_WIDTH}px`}
        height={`${FILTER_BAR_SKELETON_HEIGHT}px`}
        ry={FILTER_BAR_BORDER_RADIUS}
        rx={FILTER_BAR_BORDER_RADIUS}
      />
      <rect
        x={`${
          ourX + filterBarWidth + FILTER_BAR_INTERNAL_GUTTER * 2 + FILTER_BAR_FILTER_BUTTON_WIDTH
        }px`}
        y={`${ourY}px`}
        width={`${FILTER_BAR_BASIC_BUTTON_WIDTH}px`}
        height={`${FILTER_BAR_SKELETON_HEIGHT}px`}
        ry={FILTER_BAR_BORDER_RADIUS}
        rx={FILTER_BAR_BORDER_RADIUS}
      />
    </>
  );
};

interface TableSkeletonProps {
  rowHeight?: number;
  headerHeight?: number;
  gutter?: number;
  xOffset?: number;
  yOffset?: number;
  width?: number | DimensionGetter;
  height?: number | DimensionGetter;
}

// Default BPMTable row and header heights are 50 pixels
export const TableSkeleton = ({
  rowHeight: rowHeightProp = 50,
  headerHeight: headerHeightProp = 50,
  gutter = 10,
  xOffset = 0,
  yOffset = 0,
  width: widthProp,
  height: heightProp
}: TableSkeletonProps) => {
  const { width, height } = useContext(SkeletonContext);

  let ourWidth = width;
  if (widthProp != null) {
    ourWidth = widthProp instanceof Function ? widthProp(width) : width;
  }

  let ourHeight = height;
  if (heightProp != null) {
    ourHeight = heightProp instanceof Function ? heightProp(height) : height;
  }

  const FILTER_BAR_HEIGHT = 40;
  const FILTER_SUBMIT_WIDTH = 300;
  const FILTER_BAR_MARGIN = 5;
  const BORDER_HEIGHT = 5;

  const rowHeight = rowHeightProp - gutter;
  const headerHeight = headerHeightProp - gutter / 2 - BORDER_HEIGHT / 2;

  const nonTableHeight = FILTER_BAR_HEIGHT + FILTER_BAR_MARGIN + headerHeight + gutter + yOffset;

  const availableHeight = ourHeight - nonTableHeight;
  const numItems = Math.floor(availableHeight / (rowHeight + gutter));

  const itemData = useMemo(() => {
    let currentY = nonTableHeight;

    const items: { y: string }[] = [];
    for (let i = 0; i < numItems; ++i) {
      items.push({ y: `${currentY}px` });
      currentY += rowHeight + gutter;
    }
    return items;
  }, [rowHeight, gutter, nonTableHeight, numItems]);

  return (
    <>
      <rect
        y={yOffset}
        x={`${gutter + xOffset}px`}
        width={`${Math.max(0, ourWidth - gutter * 3 - FILTER_SUBMIT_WIDTH - xOffset)}px`}
        height={`${FILTER_BAR_HEIGHT}px`}
      />
      <rect
        y={yOffset}
        x={`${Math.max(0, ourWidth - gutter - FILTER_SUBMIT_WIDTH - xOffset)}px`}
        width={`${FILTER_SUBMIT_WIDTH}px`}
        height={`${FILTER_BAR_HEIGHT}px`}
      />
      <rect
        y={`${FILTER_BAR_HEIGHT + FILTER_BAR_MARGIN + yOffset}px`}
        x={`${gutter + xOffset}px`}
        width={`${Math.max(0, ourWidth - gutter * 2 - xOffset)}px`}
        height={`${headerHeight}px`}
      />
      <rect
        y={`${
          yOffset +
          FILTER_BAR_HEIGHT +
          FILTER_BAR_MARGIN +
          headerHeight +
          gutter / 2 -
          BORDER_HEIGHT / 2
        }px`}
        x={`${gutter + xOffset}px`}
        width={`${Math.max(0, ourWidth - gutter * 2 - xOffset)}px`}
        height={`${BORDER_HEIGHT}px`}
      />
      {itemData.map((props, i) => (
        <rect
          key={i}
          {...props}
          x={`${gutter + xOffset}px`}
          width={`${Math.max(0, ourWidth - gutter * 2 - xOffset)}px`}
          height={`${rowHeight}px`}
        />
      ))}
    </>
  );
};

interface RectSkeletonProps {
  width?: number | DimensionGetter;
  height?: number | DimensionGetter;
  x?: number | DimensionGetter;
  y?: number | DimensionGetter;
  padding?: number;
  rx?: number;
  ry?: number;
}

export const RectSkeleton = ({
  width: widthProp,
  height: heightProp,
  x = 0,
  y = 0,
  padding,
  rx = 0,
  ry = 0
}: RectSkeletonProps) => {
  const { width, height } = useContext(SkeletonContext);
  let ourWidth = width;
  if (widthProp != null) {
    ourWidth = widthProp instanceof Function ? widthProp(width) : widthProp;
  }
  let ourHeight = height;
  if (heightProp != null) {
    ourHeight = heightProp instanceof Function ? heightProp(height) : heightProp;
  }

  let ourX = x instanceof Function ? x(width) : x;
  let ourY = y instanceof Function ? y(height) : y;

  if (padding) {
    ourWidth -= padding * 2;
    ourHeight -= padding * 2;
    ourX += padding;
    ourY += padding;
  }

  return (
    <rect
      x={`${ourX}px`}
      y={`${ourY}px`}
      width={`${ourWidth}px`}
      height={`${ourHeight}px`}
      rx={`${rx}px`}
      ry={`${ry}px`}
    />
  );
};

type RadiusGetter = (width: number, height: number) => number;
interface CircleSkeletonProps {
  r?: number | RadiusGetter;
  x?: number | DimensionGetter;
  y?: number | DimensionGetter;
  padding?: number;
}

export const CircleSkeleton = ({ r: rProp, x = 0, y = 0, padding }: CircleSkeletonProps) => {
  const { width, height } = useContext(SkeletonContext);
  let ourR = Math.min(width, height) / 2;
  if (rProp != null) {
    ourR = rProp instanceof Function ? rProp(width, height) : rProp;
  }

  let ourX = x instanceof Function ? x(width) : x;
  let ourY = y instanceof Function ? y(height) : y;

  if (padding) {
    ourR -= padding * 2;
    ourX += padding;
    ourY += padding;
  }

  return <circle cx={`${ourX + ourR}px`} cy={`${ourY + ourR}px`} r={`${ourR}px`} />;
};

interface PathSkeletonProps {
  points: readonly (readonly [number, number])[];
  width?: number | DimensionGetter;
  height?: number | DimensionGetter;
  x?: number | DimensionGetter;
  y?: number | DimensionGetter;
  thickness?: number;
}

export const PathSkeleton = ({
  points,
  width: widthProp,
  height: heightProp,
  x = 0,
  y = 0,
  thickness = 5
}: PathSkeletonProps) => {
  const { width, height } = useContext(SkeletonContext);
  let ourWidth = width;
  if (widthProp != null) {
    ourWidth = widthProp instanceof Function ? widthProp(width) : widthProp;
  }
  let ourHeight = height;
  if (heightProp != null) {
    ourHeight = heightProp instanceof Function ? heightProp(height) : heightProp;
  }

  const ourX = x instanceof Function ? x(width) : x;
  const ourY = y instanceof Function ? y(height) : y;

  const pathStrings = useMemo(() => {
    const result: string[] = [];
    let lastX = points[0][0];
    let lastY = points[0][1];
    for (let i = 1; i < points.length; ++i) {
      const [nextX, nextY] = points[i];
      const x1 = lastX * ourWidth + ourX;
      const x2 = nextX * ourWidth + ourX;
      const y1 = (1 - lastY) * ourHeight + ourY;
      const y2 = (1 - nextY) * ourHeight + ourY;
      result.push(`${x1}, ${y1} ${x2}, ${y2} ${x2}, ${y2 + thickness} ${x1}, ${y1 + thickness} `);
      lastX = nextX;
      lastY = nextY;
    }
    return result;
  }, [points, thickness, ourWidth, ourHeight, ourX, ourY]);

  return (
    <>
      {pathStrings.map(path => (
        <polyline key={path} points={path} />
      ))}
    </>
  );
};
